import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  BarChart3, 
  Loader2, 
  CheckCircle, 
  TrendingUp,
  Eye,
  ThumbsUp,
  MousePointer,
  Mail,
  Calendar,
  ExternalLink,
  ArrowRight
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { apiClient } from '@/lib/api';
import { toast } from 'sonner';
import GuidedProjectLayout, { useProjectSteps } from '@/components/ios26/GuidedProjectLayout';
import { GlassCard, PillButton, SectionHeader, StatusChip } from '@/components/ios26/IOSComponents';
import '@/styles/ios26-design-system.css';

/**
 * Step 5: Résultats
 * =================
 */
function ResultsStepContent() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { completeStep, stepStatuses } = useProjectSteps();
  
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  
  useEffect(() => {
    loadAnalytics();
  }, [projectId]);
  
  const loadAnalytics = async () => {
    try {
      // iter250 P0 fix: backend route is GET /api/analytics/{project_id},
      // NOT /api/projects/{project_id}/analytics (that path never
      // existed server-side -> was always 404).
      const response = await apiClient.get(`/analytics/${projectId}`);
      setAnalytics(response.data);
      
      // Mark step as visited
      if (response.data) {
        completeStep('results');
      }
    } catch (error) {
      // Analytics might not exist yet - that's OK
      setAnalytics({
        contents_published: 0,
        total_engagement: 0,
        total_clicks: 0,
        emails_sent: 0,
        period: 'Aucune donnée'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleViewCalendar = () => {
    navigate(`/projects/${projectId}/calendar`);
  };
  
  const handleViewDetailedAnalytics = () => {
    navigate(`/projects/${projectId}/analytics`);
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }
  
  const metrics = [
    {
      label: 'Contenus publiés',
      value: analytics?.contents_published || 0,
      icon: CheckCircle,
      color: 'emerald',
      emoji: '📝'
    },
    {
      label: 'Engagement',
      value: analytics?.total_engagement || 0,
      icon: ThumbsUp,
      color: 'blue',
      emoji: '❤️'
    },
    {
      label: 'Clics',
      value: analytics?.total_clicks || 0,
      icon: MousePointer,
      color: 'purple',
      emoji: '🖱️'
    },
    {
      label: 'Emails envoyés',
      value: analytics?.emails_sent || 0,
      icon: Mail,
      color: 'amber',
      emoji: '📧'
    }
  ];
  
  const hasData = (analytics?.contents_published || 0) > 0;
  
  return (
    <div className="max-w-3xl mx-auto">
      <SectionHeader
        title="Résultats"
        subtitle="Suivez les performances de vos publications"
      />
      
      {/* Period Banner */}
      <GlassCard className="p-4 mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Calendar className="w-5 h-5 text-gray-400" />
          <span className="text-gray-600">
            Période : <strong>{analytics?.period || '30 derniers jours'}</strong>
          </span>
        </div>
        <button 
          onClick={handleViewDetailedAnalytics}
          className="text-blue-500 hover:text-blue-600 text-sm font-medium flex items-center gap-1"
        >
          Détails
          <ExternalLink className="w-4 h-4" />
        </button>
      </GlassCard>
      
      {/* Metrics Grid */}
      <div className="grid md:grid-cols-2 gap-4 mb-8">
        {metrics.map((metric, i) => {
          const Icon = metric.icon;
          const colorClasses = {
            emerald: 'bg-emerald-100 text-emerald-600',
            blue: 'bg-blue-100 text-blue-600',
            purple: 'bg-purple-100 text-purple-600',
            amber: 'bg-amber-100 text-amber-600'
          };
          
          return (
            <GlassCard key={i} className="p-5">
              <div className="flex items-center gap-4">
                <div className={cn(
                  'w-12 h-12 rounded-xl flex items-center justify-center text-2xl',
                  colorClasses[metric.color]
                )}>
                  {metric.emoji}
                </div>
                <div>
                  <p className="text-sm text-gray-500">{metric.label}</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {metric.value.toLocaleString('fr-FR')}
                  </p>
                </div>
              </div>
            </GlassCard>
          );
        })}
      </div>
      
      {/* Empty State */}
      {!hasData && (
        <GlassCard className="p-8 text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4 text-3xl">
            📊
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Pas encore de données
          </h3>
          <p className="text-gray-500 max-w-sm mx-auto mb-6">
            Vos statistiques apparaîtront ici une fois que vos premiers contenus seront publiés.
          </p>
          <PillButton
            variant="secondary"
            onClick={handleViewCalendar}
            icon={<Calendar className="w-4 h-4" />}
          >
            Voir le calendrier
          </PillButton>
        </GlassCard>
      )}
      
      {/* Has Data - Summary */}
      {hasData && (
        <GlassCard className="p-5 bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200 mb-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-500 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">Votre marketing fonctionne !</h3>
              <p className="text-sm text-gray-600">
                Continuez à publier régulièrement pour augmenter votre visibilité.
              </p>
            </div>
          </div>
        </GlassCard>
      )}
      
      {/* Congratulations / Next Steps */}
      <GlassCard className="p-6 text-center bg-gradient-to-br from-blue-500 to-purple-600 text-white">
        <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center mx-auto mb-4 text-3xl">
          🎉
        </div>
        <h3 className="text-xl font-bold mb-2">
          Configuration terminée !
        </h3>
        <p className="text-blue-100 mb-6 max-w-md mx-auto">
          Votre moteur marketing est actif. Les contenus seront publiés automatiquement 
          selon votre calendrier.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <PillButton
            variant="secondary"
            className="bg-white/20 text-white hover:bg-white/30 border-0"
            onClick={handleViewCalendar}
            icon={<Calendar className="w-4 h-4" />}
          >
            Voir le calendrier
          </PillButton>
          <PillButton
            className="bg-white text-blue-600 hover:bg-blue-50"
            onClick={() => navigate('/dashboard')}
            icon={<ArrowRight className="w-4 h-4" />}
            iconPosition="right"
          >
            Retour au tableau de bord
          </PillButton>
        </div>
      </GlassCard>
    </div>
  );
}

/**
 * Step 5 Page Wrapper
 */
export default function ResultsStep() {
  return (
    <GuidedProjectLayout>
      <ResultsStepContent />
    </GuidedProjectLayout>
  );
}
